import { useCallback, useEffect, useMemo, useState } from "react";
import { ElementItemProps } from "../types";
import { useIntl } from "react-intl";
import { useDevice } from "../../../../utils/DeviceProvider";
import { Grid, Skeleton, Typography } from "@mui/material";
import Info from "./Info";
import { ComboxElement } from "./Combox";
import { useLocale } from "../../../../locales/LocaleProvider";
import { getLabel } from "../../../../utils/utils";
import { TxtboxElement } from "./Txtbox";
import { RestoreButton, useResetContextMenu } from "../../../../utils/ResetContextMenuProvider";
import { isEqual } from "lodash-es";

export interface OSPV1Parameters {
  index: number;
  protocolItems: string[];
}

interface OSPV1Value {
  baudrate: string;
  dataBit: string;
  parity: string;
  stopBit: string;
  protocol: string;
  pin?: string;
}

const OSPV1 = ({ configKey, index, protocolItems: protocols, disabled }: Omit<ElementItemProps<OSPV1Parameters>, "elementKey" | "label">) => {
  const [active] = useState({ active: true });
  const [configIndex, setConfigIndex] = useState<number | null>(null);
  const [saved, setSaved] = useState<OSPV1Value | null>(null);
  const [baudrate, setBaudrate] = useState<string | null>(null);
  const [dataBit, setDataBit] = useState("");
  const [parity, setParity] = useState("");
  const [stopBit, setStopBit] = useState("");
  const [protocol, setProtocol] = useState("");
  const [pin, setPin] = useState("");
  const [pinVisible, setPinVisible] = useState(false);

  const { formatMessage } = useIntl();
  const {
    device: { currentValue, config },
    setCurrentValue,
  } = useDevice();
  const { locale } = useLocale();
  const { openContextMenu } = useResetContextMenu();

  const baudrateItems = useMemo(() => {
    return ["300", "600", "1200", "2400", "4800", "9600", "14400", "19200", "28800", "38400", "56000", "57600", "115200"].map((item) => ({
      value: item,
      label: item,
    }));
  }, []);
  const dataBitItems = useMemo(() => {
    return ["7", "8"].map((item) => ({
      value: item,
      label: item,
    }));
  }, []);
  const parityItems = useMemo(() => {
    return [
      { value: "0", label: { hu: "Nincs", en: "None" } },
      { value: "1", label: { hu: "Páros", en: "Even" } },
      { value: "2", label: { hu: "Páratlan", en: "Odd" } },
    ].map(({ value, label }) => ({ value, label: getLabel(label, locale) }));
  }, [locale]);
  const stopBitItems = useMemo(() => {
    return ["1", "2"].map((item) => ({
      value: item,
      label: item,
    }));
  }, []);
  const protocolItems = useMemo(() => {
    return protocols.map((item) => ({
      value: item,
      label: item,
    }));
  }, [protocols]);

  const needPin = useCallback((protocol: string) => ["DDCMP", "DEX"].includes(protocol), []);

  const onChange = useCallback(
    ({
      baudrate,
      dataBit,
      parity,
      stopBit,
      protocol,
      pin,
    }: {
      baudrate?: string;
      dataBit?: string;
      parity?: string;
      stopBit?: string;
      protocol?: string;
      pin?: string;
    }) => {
      if (configIndex === null) {
        return;
      }
      if (!currentValue[configKey].items[configIndex].value) {
        currentValue[configKey].items[configIndex].value = {};
      }
      if (baudrate !== undefined) {
        currentValue[configKey].items[configIndex].value.baudrate = baudrate;
      }
      if (dataBit !== undefined) {
        currentValue[configKey].items[configIndex].value.dataBit = dataBit;
      }
      if (parity !== undefined) {
        currentValue[configKey].items[configIndex].value.parity = parity;
      }
      if (stopBit !== undefined) {
        currentValue[configKey].items[configIndex].value.stopBit = stopBit;
      }
      if (protocol !== undefined) {
        currentValue[configKey].items[configIndex].value.protocol = protocol;
        if (!needPin(protocol)) {
          delete currentValue[configKey].items[configIndex].value.pin;
        }
      }
      if (pin !== undefined) {
        currentValue[configKey].items[configIndex].value.pin = pin;
      }
      setCurrentValue(currentValue);
    },
    [setCurrentValue, currentValue, configKey, configIndex, needPin]
  );

  const handleProtocolChange = useCallback(
    (protocol: string) => {
      setProtocol(protocol);
      setPinVisible(needPin(protocol));
    },
    [needPin]
  );

  useEffect(() => {
    active.active = true;

    return () => {
      active.active = false;
    };
  }, [active]);

  useEffect(() => {
    if (!currentValue) {
      return;
    }
    const cIndex = currentValue[configKey]?.items?.findIndex(({ type, key }: { type: string; key: string }) => type === "OSPV1" && key === `OSPV1-${index}`) ?? null;
    setConfigIndex(cIndex);
  }, [configKey, currentValue, index]);

  useEffect(() => {
    if (!currentValue || !config || configIndex === null || configIndex === -1) {
      return;
    }
    const svd = config.toolboxConfig[configKey]?.items?.[configIndex].value ?? null;
    setSaved(svd);
    const { baudrate, dataBit, parity, stopBit, protocol, pin } = currentValue[configKey].items[configIndex]?.value ?? {};
    setBaudrate(baudrate);
    setDataBit(dataBit);
    setParity(parity);
    setStopBit(stopBit);
    setProtocol(protocol);
    setPinVisible(needPin(protocol));
    setPin(pin);
  }, [active, currentValue, config, configIndex, configKey, needPin]);

  if (baudrate === null) {
    return (
      <Grid container spacing={1}>
        <Grid item>
          <Info text={`${index + 1}.`} inlineTitle />
        </Grid>
        <Grid item>
          <Skeleton variant="rectangular" height={40} sx={{ width: 100, borderRadius: 1 }} />
        </Grid>
        <Grid item>
          <Skeleton variant="rectangular" height={40} sx={{ width: 100, borderRadius: 1 }} />
        </Grid>
        <Grid item>
          <Skeleton variant="rectangular" height={40} sx={{ width: 100, borderRadius: 1 }} />
        </Grid>
        <Grid item>
          <Skeleton variant="rectangular" height={40} sx={{ width: 100, borderRadius: 1 }} />
        </Grid>
        <Grid item>
          <Skeleton variant="rectangular" height={40} sx={{ width: 150, borderRadius: 1 }} />
        </Grid>
      </Grid>
    );
  }

  if (configIndex === -1) {
    return <Typography>{formatMessage({ defaultMessage: "Config not found." })}</Typography>;
  }

  return (
    <Grid container spacing={1} sx={{ alignItems: "flex-end" }}>
      <Grid item>
        <Info text={`${index + 1}.`} inlineTitle />
      </Grid>
      <Grid item>
        <ComboxElement
          value={baudrate ?? ""}
          label={formatMessage({ defaultMessage: "Baudrate" })}
          items={baudrateItems}
          onChange={setBaudrate}
          onBlur={() => onChange({ baudrate })}
          edited={saved?.baudrate !== baudrate}
          disabled={disabled}
          onContextMenu={saved && saved.baudrate !== baudrate ? (e) => openContextMenu(e, `${configKey}.items.${configIndex}.value.baudrate`) : undefined}
        />
      </Grid>
      <Grid item>
        <ComboxElement
          value={dataBit ?? ""}
          label={formatMessage({ defaultMessage: "Data bit" })}
          items={dataBitItems}
          onChange={setDataBit}
          onBlur={() => onChange({ dataBit })}
          edited={saved?.dataBit !== dataBit}
          disabled={disabled}
          onContextMenu={saved && saved.dataBit !== dataBit ? (e) => openContextMenu(e, `${configKey}.items.${configIndex}.value.dataBit`) : undefined}
        />
      </Grid>
      <Grid item>
        <ComboxElement
          value={parity ?? ""}
          label={formatMessage({ defaultMessage: "Parity" })}
          items={parityItems}
          onChange={setParity}
          onBlur={() => onChange({ parity })}
          edited={saved?.parity !== parity}
          disabled={disabled}
          onContextMenu={saved && saved.parity !== parity ? (e) => openContextMenu(e, `${configKey}.items.${configIndex}.value.parity`) : undefined}
        />
      </Grid>
      <Grid item>
        <ComboxElement
          value={stopBit ?? ""}
          label={formatMessage({ defaultMessage: "Stop bit" })}
          items={stopBitItems}
          onChange={setStopBit}
          onBlur={() => onChange({ stopBit })}
          edited={saved?.stopBit !== stopBit}
          disabled={disabled}
          onContextMenu={saved && saved.stopBit !== stopBit ? (e) => openContextMenu(e, `${configKey}.items.${configIndex}.value.stopBit`) : undefined}
        />
      </Grid>
      <Grid item>
        <ComboxElement
          value={protocol ?? ""}
          label={formatMessage({ defaultMessage: "Protocol" })}
          items={protocolItems}
          onChange={handleProtocolChange}
          onBlur={() => onChange({ protocol })}
          edited={saved?.protocol !== protocol}
          disabled={disabled}
          onContextMenu={saved && saved.protocol !== protocol ? (e) => openContextMenu(e, `${configKey}.items.${configIndex}.value.protocol`) : undefined}
        />
      </Grid>
      {pinVisible ? (
        <Grid item>
          <TxtboxElement
            value={pin ?? ""}
            label={formatMessage({ defaultMessage: "PIN" })}
            onChange={setPin}
            onBlur={() => onChange({ pin })}
            edited={saved?.pin !== pin}
            disabled={disabled}
            onContextMenu={saved && saved.pin !== pin ? (e) => openContextMenu(e, `${configKey}.items.${configIndex}.value.pin`) : undefined}
          />
        </Grid>
      ) : null}
      {(
        !saved
          ? baudrate || dataBit || parity || stopBit || protocol || (pinVisible && pin)
          : !isEqual(
              saved,
              Object.fromEntries(
                [
                  ["baudrate", baudrate],
                  ["dataBit", dataBit],
                  ["parity", parity],
                  ["stopBit", stopBit],
                  ["protocol", protocol],
                  pinVisible ? ["pin", pin] : null,
                ].filter((i) => !!i) as [string, string][]
              )
            )
      ) ? (
        <Grid item>
          <RestoreButton path={`${configKey}.items.${configIndex}.value`} saved={saved} />
        </Grid>
      ) : null}
    </Grid>
  );
};

export default OSPV1;
